import { Form } from '@panwds/react-form'
import type { VFC } from 'react'
import { useContext, useMemo } from 'react'
import type { ComponentDef, ManifestFlags } from '../../renderer'
import type { CommonStoreState } from '../../store'
import ErrorBanner from '../common/ErrorBanner'
import ActivationBanner from '../common/ActivationBanner'
import { VIEW_MANAGE_TENANT, APP_ID_IOT } from '../common/GlobalConstants'
import { useCallback, useState, useEffect } from 'react'
import SubscriptionService from '../../services/SubscriptionService'
import { actions, history } from '@sparky/framework'
import { ACTIVATION_UI_ERROR_CODES, ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP } from '../../ErrorCodes'
import ScreenLoader from '../common/ScreenLoader'
import { appIdsEligibleForActivation } from '../common/utils'
import GlobalContext from '../../../common/context/GlobalContext'
import { LoadTreeDataAsync } from '../../../common/actions/GlobalActions'
import { hasCommonDrawer } from '../../../common/utils/Sparky'

type DefaultActivationFormProps = {
  children: ComponentDef
  state: CommonStoreState
  primaryButton: {
    text: string
  }
}

const UProfileActivationForm: VFC<DefaultActivationFormProps> = ({ children, state, primaryButton }) => {
  const { licenses, tsg, instances, isHashFullyActivated, manifest } = state
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [infoMsg, setInfoMsg] = useState<string>('')
  const [activationInProgress, setActivationInProgress] = useState(false)

  const { dispatch } = useContext(GlobalContext)

  const appIdsToBeActivated = useMemo(() => {
    return appIdsEligibleForActivation(licenses, manifest?.primary_app_id as string, manifest?.flags as ManifestFlags)
  }, [licenses, manifest])

  useEffect(() => {
    if (isHashFullyActivated) {
      setInfoMsg(ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP.HASH_FULLY_ACTIVATED)
    }
    else {
      setInfoMsg('')
    }
  }, [licenses, tsg, setInfoMsg, instances, isHashFullyActivated, appIdsToBeActivated])

  const calculateNewTsg = useCallback((currentTsgName: string, tsgName: string, tsgDropDownDetails: any, count = 0): string => {
    const isNewTsgName = tsgDropDownDetails.some((each: any) => {
      return each.display_name === tsgName
    })
    if (!isNewTsgName) {
      return tsgName
    }
    else {
      count++
      return calculateNewTsg(currentTsgName, `${currentTsgName} - ${count}`, tsgDropDownDetails, count)
    }
  }, [])

  const onPrimaryBtnClick = useCallback(async (values: any) => {
    setActivationInProgress(true)
    const tsgData = {
      tsg_id: values.tsg?.id,
      tsg_name: values.tsg?.value,
      isError: false
    }
    // day zero case, user create new TSG
    // if (activationData?.tsgCreateList && isNonemptyArray(activationData?.tsgCreateList)) {
    //   const createTsgs = async (tsgCreateList: { id?: string, display_name: string, parent_id?: string }[]) => {
    //     const _tsgCreateList = Array.from(tsgCreateList)
    //     let parent_id: string | undefined
    //     // iteratively create tsg tree
    //     for (const each of _tsgCreateList) {
    //       const payload = {
    //         display_name: each.display_name,
    //         parent_id,
    //       }
    //       const { data } = await TenancyService.createTenant.post(payload, parent_id ?? TSG_NONE.id).catch((err) => {
    //         setErrorMsg(ACTIVATION_UI_ERROR_CODES.CREATE_TENANT_FAILED)
    //         setActivationInProgress(false)
    //         tsgData.isError = true
    //         return { data: undefined }
    //       })
    //       each.id = data.id
    //       each.display_name = data.display_name
    //       parent_id = data.id
    //     }
    //     const recipient = _tsgCreateList[_tsgCreateList.length - 1]
    //     tsgData.tsg_id = recipient.id
    //     tsgData.tsg_name = recipient.display_name
    //   }
    //   const { tsgCreateList } = activationData
    //   await createTsgs(tsgCreateList)
    // }
    // console.log(values)

    if (tsgData.isError) {
      return
    }
    const _subscription = values['subscriptions']
    _subscription?.forEach((sub: { app_id: string; domain: any }) => {
      if (sub.app_id === APP_ID_IOT) {
        sub.domain = values[`${APP_ID_IOT}-subdomain`]
      }
      else {
        delete sub.domain
      }
    })
    const dps = values['deployment_profiles']?.map((dp: any) => {
      return {
        ...dp,
        deploymentProfileId: Number(dp.deploymentProfileId),
        subscriptions: dp.subscriptions?.map((s: any) => {
          if (!s.package_version) {
            return {
              ...s
            }
          }
          else {
            return {
              ...s,
              package_version: `${s.package_version}`
            }
          }

        })
      }
    })
    const request = {
      platform_region: values['region'].id,
      support_account_id: values['csp'].id,
      support_account_name: values['csp'].description,
      deployment_profiles: dps,
      tsg_name: tsgData.tsg_name,
      subscriptions: _subscription
    }
    //
    // console.log(request)
    // setActivationInProgress(false)
    SubscriptionService.deploymentProfiles.post(request, tsgData.tsg_id).then(async (data) => {
      await LoadTreeDataAsync(dispatch)
      await actions.switchTsg(tsgData.tsg_id)
      setActivationInProgress(false)
      if (!hasCommonDrawer()) {
        history.push('/settings/tenants')
      }
      else {
        history.push('/')
        actions.openCommonModule(VIEW_MANAGE_TENANT, tsgData.tsg_id)
      }

    }).catch((err) => {
      setActivationInProgress(false)
      setErrorMsg(err?.message || ACTIVATION_UI_ERROR_CODES.ACTIVATION_FAILED)
    })

  }, [dispatch])

  const clearErrorMessage = useCallback(() => {
    setErrorMsg('')
  }, [])

  const groupedChildren = useMemo(() => {
    const subChildren = children?.props?.children
    const groupedChilderen = new Map()
    if (subChildren && Array.isArray(subChildren)) {
      subChildren.forEach(child => {
        const key = child?.props?.group || 1
        const collection = groupedChilderen.get(key) || []
        if (collection.length) {
          collection.push(child)
        }
        else {
          groupedChilderen.set(key, [child])
        }
      })
    }
    else {
      groupedChilderen.set(1, subChildren)
    }
    return groupedChilderen
  }, [children])

  return (
    <div className='cm-w-[1000px]'>
      {activationInProgress && <ScreenLoader />}
      {infoMsg && <ActivationBanner infoMessage={infoMsg} appearance='standard' type='page' />}
      {errorMsg && <ErrorBanner errorCode={errorMsg} callback={clearErrorMessage} />}
      <div className='cm-flex cm-flex-row'>
        <Form onSubmit={onPrimaryBtnClick} mode='onBlur' formProps={{ style: { height: '100%', width: '100%' } }}>
          <div>
            {groupedChildren.get(1)}
          </div>
          {groupedChildren.has(2) && <div className='cm-mt-2'>
            {groupedChildren.get(2)}
          </div>}
          {groupedChildren.has(3) && <div className='cm-mt-3'>
            {groupedChildren.get(3)}
          </div>}
        </Form>
      </div>
    </div>
  )
}
export default UProfileActivationForm
